import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";

const s = (v) => (v !== null && v !== undefined ? String(v) : "");
const b = (v) => v === true || v === "yes" || v === "true";
const hasPath = (v) => !!(v && v !== "string");

/**
 * Updates entity primary + KYC data.
 * POST api/Onboarding/update-entity
 */
export const updateEntityAction = async (_entityId, entityData) => {
  try {
    const now = new Date().toISOString();
    const e = entityData;

    // Build gstList — preserve gstId, isGST, isGSTDef from existing entries
    const gstList = Array.isArray(e.gstList)
      ? e.gstList.map((g, i) => ({
          id: g.gstId || g.id || i + 1,
          gstNumber: s(g.gstNumber),
          state: s(g.state || g.gstState),
          gstAddress: s(g.gstAddress),
          gstCertificatePath: s(g.gstCertificatePath),
          isGST: hasPath(g.gstCertificatePath),
          isGSTDef: g.isGSTDef === true,
        }))
      : [];

    // Build uploadedList from existing document paths (for update, paths are already stored)
    const uploadedList = Array.isArray(e.uploadedList) ? e.uploadedList : [];

    const payload = {
      // ── Identifiers ──────────────────────────────────────────────────────
      entityPrimaryId: e.entityPrimaryId || e.id || 0,
      kycId: s(e.kycId),

      // ── KYC top-level fields ─────────────────────────────────────────────
      tradeName: s(e.tradeName),
      pinCode: s(e.pinCode),
      city: s(e.city),
      state: s(e.state || e.State),
      address: s(e.address),
      natureOfBusiness: s(e.natureOfBusiness),
      invoiceRequired: b(e.invoiceRequired ?? e.isInvoiceRequired),
      entityType: s(e.entityType),

      // ── Contact person ────────────────────────────────────────────────────
      contactPersonDetails: {
        contactPersonName: s(e.contactPersonName),
        designation: s(e.contactPersonDesignation || e.designation),
        mobileNumber: s(e.contactPersonMobile || e.mobileNumber),
        contactPersonEmail: s(e.contactEmail || e.contactPersonEmail),
      },

      // ── Proprietorship ────────────────────────────────────────────────────
      proprietorshipDetails:
        e.entityType === "proprietorship"
          ? {
              aadharCardNumber: s(e.aadharCardNumber),
              aadharCardPath: s(e.aadharCardPath),
              aadharCardUploaded: hasPath(e.aadharCardPath),
              isAadharCard: hasPath(e.aadharCardPath),
              isAadharCardDef: e.deficiencyMap?.doc_aadhar ? true : false,
              panCardNumber: s(e.panCardNumber || e.CompanypanNumber),
              panCardPath: s(e.panCardPath),
              panCardUploaded: hasPath(e.panCardPath),
              isPANCard: hasPath(e.panCardPath),
              isPANCardDef: e.deficiencyMap?.doc_pan ? true : false,
            }
          : {
              aadharCardNumber: "",
              aadharCardPath: "",
              aadharCardUploaded: false,
              isAadharCard: false,
              isAadharCardDef: false,
              panCardNumber: "",
              panCardPath: "",
              panCardUploaded: false,
              isPANCard: false,
              isPANCardDef: false,
            },

      // ── Aadhar details ────────────────────────────────────────────────────
      adharDetails: {
        aadharCardNumber: s(e.aadharCardNumber),
        aadharCardPath: s(e.aadharCardPath),
        aadharCardUploaded: hasPath(e.aadharCardPath),
        isAadharCard: hasPath(e.aadharCardPath),
        isAadharCardDef: e.deficiencyMap?.doc_aadhar ? true : false,
      },

      // ── PAN details ───────────────────────────────────────────────────────
      panDetails: {
        panCardNumber: s(e.panCardNumber || e.CompanypanNumber),
        panCardPath: s(e.panCardPath),
        panCardUploaded: hasPath(e.panCardPath),
        isPANCard: hasPath(e.panCardPath),
        isPANCardDef: e.deficiencyMap?.doc_pan ? true : false,
      },

      // ── Partnership ───────────────────────────────────────────────────────
      partnershipDetails: {
        partnershipDeedPath: s(e.partnershipDeedPath),
        partnershipDeedUploaded: hasPath(e.partnershipDeedPath),
        isPartnershipDeed: hasPath(e.partnershipDeedPath),
        isPartnershipDeedDef: e.deficiencyMap?.doc_partnershipDeed
          ? true
          : false,
        aadharNumberOfBothPartners: s(e.aadharNumberOfBothPartners),
        aadharOfBothPartnersPath: s(e.aadharOfBothPartnersPath),
        aadharOfBothPartnersUploaded: hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartners: hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartnersDef: e.deficiencyMap?.doc_aadharBoth
          ? true
          : false,
      },

      // ── LLP ───────────────────────────────────────────────────────────────
      llpDetails: {
        llpCrtificateNumber: s(e.llpCrtificateNumber),
        uploadLLPCertificatePath: s(e.uploadLLPCertificatePath),
        llpCertificateUploaded: hasPath(e.uploadLLPCertificatePath),
        isLLPCertificate: hasPath(e.uploadLLPCertificatePath),
        isLLPCertificateDef: e.deficiencyMap?.doc_llpCertificate ? true : false,
        partnershipDeedPath: s(e.partnershipDeedPath),
        partnershipDeedUploaded: hasPath(e.partnershipDeedPath),
        isPartnershipDeed: hasPath(e.partnershipDeedPath),
        isPartnershipDeedDef: e.deficiencyMap?.doc_partnershipDeed
          ? true
          : false,
        aadharNumberOfBothPartners: s(e.aadharNumberOfBothPartners),
        aadharOfBothPartnersPath: s(e.aadharOfBothPartnersPath),
        aadharOfBothPartnersUploaded: hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartners: hasPath(e.aadharOfBothPartnersPath),
        isAadharOfBothPartnersDef: e.deficiencyMap?.doc_aadharBoth
          ? true
          : false,
      },

      // ── Private / Public Limited ──────────────────────────────────────────
      privatePublicLimitedCompanyDetails: {
        certificateOfIncorporationNumber: s(e.certificateOfIncorporationNumber),
        certificateOfIncorporationPath: s(e.certificateOfIncorporationPath),
        certificateOfIncorporationUploaded: hasPath(
          e.certificateOfIncorporationPath,
        ),
        isCertificateOfIncorporation: hasPath(e.certificateOfIncorporationPath),
        isCertificateOfIncorporationDef: e.deficiencyMap?.doc_coi
          ? true
          : false,
        directorPANNumber: s(e.directorPANNumber),
        directorPANPath: s(e.directorPANPath),
        directorPanUploaded: hasPath(e.directorPANPath),
        isDirectorPan: hasPath(e.directorPANPath),
        isDirectorPanDef: e.deficiencyMap?.doc_directorPan ? true : false,
        directorAadharNumber: s(e.directorAadharNumber),
        directorAadharPath: s(e.directorAadharPath),
        directorAadharUploaded: hasPath(e.directorAadharPath),
        isDirectorAadhar: hasPath(e.directorAadharPath),
        isDirectorAadharDef: e.deficiencyMap?.doc_directorAadhar ? true : false,
      },

      // ── Trust ─────────────────────────────────────────────────────────────
      trustDetails: {
        trustDeedNumber: s(e.trustDeedNumber),
        trustDeedPath: s(e.trustDeedPath),
        trustDeedUploaded: hasPath(e.trustDeedPath),
        isTrustDeed: hasPath(e.trustDeedPath),
        isTrustDeedDef: e.deficiencyMap?.doc_trustDeed ? true : false,
        trustPanNumber: s(e.panOfTrustNumber),
        trustPanPath: s(e.panOfTrustPath),
        trustPanUploaded: hasPath(e.panOfTrustPath),
        isPANOfTrust: hasPath(e.panOfTrustPath),
        isPANOfTrustDef: e.deficiencyMap?.doc_panTrust ? true : false,
        trusteePanNumber: s(e.panOfTrusteeNumber),
        trusteePanPath: s(e.panOfTrusteePath),
        trusteePanUploaded: hasPath(e.panOfTrusteePath),
        isPANOfTrustee: hasPath(e.panOfTrusteePath),
        isPANOfTrusteeDef: e.deficiencyMap?.doc_panTrustee ? true : false,
        trusteeAadharNumber: s(e.aadharNumberOfTrustee),
        trusteeAadharPath: s(e.aadharOfTrusteePath),
        trusteeAadharUploaded: hasPath(e.aadharOfTrusteePath),
        isAadharOfTrustee: hasPath(e.aadharOfTrusteePath),
        isAadharOfTrusteeDef: e.deficiencyMap?.doc_aadharTrustee ? true : false,
      },

      // ── MSME ──────────────────────────────────────────────────────────────
      msmeDetails: {
        isRegisteredUnderMSME: b(e.isMSMERegistered ?? e.isRegisteredUnderMSME),
        msmeRegistrationNumber: s(e.msmeNumber || e.msmeRegistrationNumber),
        msmeCertificatePath: s(e.msmeCertificatePath),
        msmeCertificateUploaded: hasPath(e.msmeCertificatePath),
        isMSMECertificate: hasPath(e.msmeCertificatePath),
        isMSMECertificateDef: e.deficiencyMap?.doc_msme_certificate
          ? true
          : false,
      },

      // ── Utility bill ──────────────────────────────────────────────────────
      utilityBillDetails: {
        utilityType: s(e.utilityType),
        providerName: s(e.utilityProviderName || e.providerName),
        accountOrConsumerNumber: s(
          e.utilityAccountNumber || e.accountOrConsumerNumber,
        ),
        addressOnUtilityBill: s(e.utilityAddress || e.addressOnUtilityBill),
        utilityBillDocumentPath: s(e.utilityBillDocumentPath),
        utilityBillDocumentUploaded: hasPath(e.utilityBillDocumentPath),
        isUtilityBillDocument: hasPath(e.utilityBillDocumentPath),
        isUtilityBillDocumentDef: e.deficiencyMap?.doc_utility_bill
          ? true
          : false,
      },

      // ── Bank details ──────────────────────────────────────────────────────
      bankDetails: {
        provideBankDetails: b(e.hasBankDetails ?? e.provideBankDetails),
        bankName: s(e.bankName),
        accountNumber: s(e.accountNumber),
        ifscCode: s(e.ifscCode),
        accountType: s(e.accountType),
        branchName: s(e.branchName),
        beneficiaryName: s(e.beneficiaryName),
        relationWithBeneficiary: s(e.relationWithBeneficiary),
        passbookOrCancelledChequePath: s(e.passbookOrCancelledChequePath),
        passbookOrCancelledChequeUploaded: hasPath(
          e.passbookOrCancelledChequePath,
        ),
        isPassbookOrCancelledCheque: hasPath(e.passbookOrCancelledChequePath),
        isPassbookOrCancelledChequeDef: e.deficiencyMap?.doc_bank_proof
          ? true
          : false,
        uploadSignedFormPath: s(e.uploadSignedFormPath),
        signedFormUploaded: hasPath(e.uploadSignedFormPath),
        isSignedForm: hasPath(e.uploadSignedFormPath),
        isSignedFormDef: e.deficiencyMap?.third_party_file ? true : false,
      },

      // ── GST ───────────────────────────────────────────────────────────────
      isGSTRegistered: b(e.isGSTRegistered),
      gstList: b(e.isGSTRegistered) ? gstList : [],

      // ── Uploaded file list ────────────────────────────────────────────────
      uploadedList,

      // ── Timestamps ───────────────────────────────────────────────────────
      createdBy: s(e.createdBy),
      createdOn: s(e.createdOn) || now,
      modifiedBy: s(e.modifiedBy) || "Admin",
      modifiedOn: now,
      isDocumentUploaded: e.isDocumentUploaded === true,
      isSaveAsDraft: e.isSaveAsDraft === true,

      // ── Primary / entity-level fields ────────────────────────────────────
      parentCode: s(e.parentCode || e.parentId),
      parentName: s(e.parentName),
      entityName: s(e.entityName),
      entityCategory: s(e.entityCategory),
      entityLOBType: s(e.entityLOBType || e.lobType),
      aaDepartmentFunction: s(e.aaDepartmentFunction),
      emailAddress: s(e.emailAddress),
      spoC_EmailAddress: s(e.spocEmailAddress || e.spoC_EmailAddress),
      kyC_URL: s(e.kyC_URL),
      requireKYC: b(e.requireKYC),
    };

    console.log("[UpdateEntity] payload:", payload);

    const response = await apiClient.post(
      ATP_API_URL.UPDATE_FORM_ALL_DATA,
      payload,
    );

    if (response?.status === 200) {
      if (response.data?.success === false) {
        return {
          success: false,
          message: response.data.message || "Failed to update entity",
        };
      }
      return {
        success: true,
        data: response.data,
        message: "Entity updated successfully",
      };
    }

    return { success: false, message: "Failed to update entity" };
  } catch (error) {
    console.error("[UpdateEntity] error:", error);
    return {
      success: false,
      message: error.message || "Failed to update entity",
      error,
    };
  }
};
